import React, { useEffect, useState, useCallback, useRef } from "react";
import spotlightArrowSvg from "../assets/images/spotlight-arrow.png";
import { createPortal } from "react-dom";

/** Compare two DOMRect-like objects within a tolerance */
function rectsEqual(a, b, tolerance = 1) {
    return (
        Math.abs(a.x - b.x) <= tolerance &&
        Math.abs(a.y - b.y) <= tolerance &&
        Math.abs(a.width - b.width) <= tolerance &&
        Math.abs(a.height - b.height) <= tolerance
    );
}

export const SpotlightOverlay = ({
    targetSelector,
    active,
    onDismiss,
    onTargetClick,
    message,
    highlightSelector,
    onNext,
    onSkip,
    targetPadding,
    targetRadius,
    scrollBlock,
    messageMaxWidth,
    position = "top",
    nextLabel = "Next",
    skipLabel = "Skip",
    passThrough = false,
}) => {
    const [rect, setRect] = useState(null);
    const [highlightRect, setHighlightRect] = useState(null);
    const settledRef = useRef(false);
    const settledAtRef = useRef(0);
    const rectRef = useRef(null);
    const highlightRectRef = useRef(null);

    const syncRects = useCallback(
        (targetEl, highlightEl = null) => {
            if (!targetEl) return;

            const nextRect = targetEl.getBoundingClientRect();
            if (!rectRef.current || !rectsEqual(nextRect, rectRef.current, 0.5)) {
                rectRef.current = nextRect;
                setRect(nextRect);
            }

            if (!highlightSelector) return;

            if (!highlightEl) {
                highlightRectRef.current = null;
                setHighlightRect(null);
                return;
            }

            const nextHighlightRect = highlightEl.getBoundingClientRect();
            if (
                !highlightRectRef.current ||
                !rectsEqual(nextHighlightRect, highlightRectRef.current, 0.5)
            ) {
                highlightRectRef.current = nextHighlightRect;
                setHighlightRect(nextHighlightRect);
            }
        },
        [highlightSelector]
    );

    const measure = useCallback(() => {
        if (!settledRef.current) return;
        const el = document.querySelector(targetSelector);
        const hlEl = highlightSelector ? document.querySelector(highlightSelector) : null;
        syncRects(el, hlEl);
    }, [highlightSelector, syncRects, targetSelector]);

    useEffect(() => {
        if (!active) {
            setRect(null);
            setHighlightRect(null);
            settledRef.current = false;
            rectRef.current = null;
            highlightRectRef.current = null;
            return;
        }

        let cancelled = false;
        let resizeObserver = null;
        let rafId = null;
        let trackingRafId = null;

        const startStabilityLoop = (el, hlEl) => {
            // Always scroll the target into view — block:"center" is effectively
            // a no-op when the element is already centered, and it guarantees
            // off-screen targets (like Add Convertible after a tab switch) are visible.
            el.scrollIntoView({
                behavior: "smooth",
                block: scrollBlock ?? "center",
                inline: "nearest",
            });

            let lastRect = null;
            let stableFrames = 0;
            const requiredStableFrames = 3;
            const maxFrames = 60; // ~1s fallback
            let frameCount = 0;

            const check = () => {
                if (cancelled) return;
                frameCount++;
                const currentRect = el.getBoundingClientRect();

                if (lastRect && rectsEqual(currentRect, lastRect)) {
                    stableFrames++;
                } else {
                    stableFrames = 0;
                }
                lastRect = currentRect;

                if (stableFrames >= requiredStableFrames || frameCount >= maxFrames) {
                    // Settled — commit the measurement
                    settledRef.current = true;
                    settledAtRef.current = Date.now();
                    syncRects(el, hlEl);

                    // Start observing for resize changes
                    resizeObserver = new ResizeObserver(() => {
                        if (!cancelled) measure();
                    });
                    resizeObserver.observe(el);
                    if (hlEl) resizeObserver.observe(hlEl);

                    const trackLayoutShift = () => {
                        if (cancelled || !settledRef.current) return;
                        syncRects(el, hlEl);
                        trackingRafId = requestAnimationFrame(trackLayoutShift);
                    };

                    trackingRafId = requestAnimationFrame(trackLayoutShift);
                } else {
                    rafId = requestAnimationFrame(check);
                }
            };

            rafId = requestAnimationFrame(check);
        };

        // Poll for element existence
        let attempts = 0;
        const maxAttempts = 25;
        const tryFind = () => {
            if (cancelled) return;
            const el = document.querySelector(targetSelector);
            const hlEl = highlightSelector ? document.querySelector(highlightSelector) : null;

            if (!el || (highlightSelector && !hlEl)) {
                if (attempts < maxAttempts) {
                    attempts++;
                    setTimeout(tryFind, 200);
                }
                return;
            }

            startStabilityLoop(el, hlEl);
        };

        const timeout = setTimeout(tryFind, 50);

        const handleScrollResize = () => {
            if (!settledRef.current) return;
            if (Date.now() - settledAtRef.current < 300) return;
            measure();
        };
        window.addEventListener("resize", handleScrollResize);
        window.addEventListener("scroll", handleScrollResize, true);

        return () => {
            cancelled = true;
            clearTimeout(timeout);
            if (rafId != null) cancelAnimationFrame(rafId);
            if (trackingRafId != null) cancelAnimationFrame(trackingRafId);
            settledRef.current = false;
            rectRef.current = null;
            highlightRectRef.current = null;
            window.removeEventListener("resize", handleScrollResize);
            window.removeEventListener("scroll", handleScrollResize, true);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [active, measure, targetSelector, highlightSelector]);

    if (!active || !rect) return null;

    const padding = targetPadding ?? 6;
    const radius = targetRadius ?? 8;
    const cutout = {
        x: rect.x - padding,
        y: rect.y - padding,
        w: rect.width + padding * 2,
        h: rect.height + padding * 2,
        r: radius,
    };

    const hlPadding = 8;
    const hlCutout = highlightRect
        ? (() => {
            const x = highlightRect.x - hlPadding;
            const y = highlightRect.y - hlPadding;
            const rawBottom = highlightRect.y + highlightRect.height + hlPadding;
            const clippedBottom = Math.min(rawBottom, window.innerHeight);
            return {
                x,
                y,
                w: highlightRect.width + hlPadding * 2,
                h: Math.max(0, clippedBottom - y),
                r: 12,
            };
        })()
        : null;

    const handleClick = (e) => {
        // e.preventDefault(); // Might block actual selection logic
        e.stopPropagation();

        const x = e.clientX;
        const y = e.clientY;
        const tolerance = 2; // Add a small buffer

        if (
            x >= cutout.x - tolerance &&
            x <= cutout.x + cutout.w + tolerance &&
            y >= cutout.y - tolerance &&
            y <= cutout.y + cutout.h + tolerance
        ) {
            if (onTargetClick) onTargetClick();
            const target = document.querySelector(targetSelector);
            if (target) target.click();
            if (!onTargetClick) onDismiss();
        } else {
            onDismiss();
        }
    };

    const arrowWidth = 24;
    const arrowHeight = 26;
    const cardMaxW = messageMaxWidth ?? 300;
    const edgePadding = 16;

    // Coordinate calculation based on position
    let arrowTop, arrowLeft, arrowTransform;
    let msgStyle = { position: "absolute", maxWidth: cardMaxW, animation: "spotlight-fade-in 0.4s ease-out 0.3s both" };

    let activePosition = position;
    if (position === "auto") {
        const spaceTop = cutout.y;
        const spaceBottom = window.innerHeight - (cutout.y + cutout.h);
        const spaceLeft = cutout.x;
        const spaceRight = window.innerWidth - (cutout.x + cutout.w);

        // Threshold for a comfortable message box (height ~120px + arrow 30px)
        const vThreshold = 180;

        if (spaceBottom > vThreshold) {
            activePosition = "bottom";
        } else if (spaceTop > vThreshold) {
            activePosition = "top";
        } else if (window.innerWidth > 768) { // Use side placement only on tablet/desktop
            activePosition = spaceRight > spaceLeft ? "right" : "left";
        } else {
            // Mobile fallback: pick the larger vertical space
            activePosition = spaceBottom > spaceTop ? "bottom" : "top";
        }
    }

    if (activePosition === "bottom") {
        arrowTop = cutout.y + cutout.h + 2;
        const rawMsgLeft = cutout.x + cutout.w / 2 - 20;
        arrowLeft = rawMsgLeft + 4;
        msgStyle.left = Math.max(edgePadding, Math.min(rawMsgLeft, window.innerWidth - cardMaxW - edgePadding));
        msgStyle.top = arrowTop + arrowHeight + 4;
        arrowTransform = "rotate(180deg) scaleX(-1)";
    } else if (activePosition === "left") {
        arrowLeft = cutout.x - arrowWidth - 2;
        const rawArrowTop = cutout.y + cutout.h / 2 - arrowHeight / 2;
        arrowTop = rawArrowTop;
        msgStyle.right = window.innerWidth - arrowLeft + 4;
        msgStyle.top = Math.max(edgePadding, Math.min(rawArrowTop - 20, window.innerHeight - 150));
        arrowTransform = "rotate(-90deg) scaleX(-1)";
    } else if (activePosition === "right") {
        arrowLeft = cutout.x + cutout.w + 2;
        const rawArrowTop = cutout.y + cutout.h / 2 - arrowHeight / 2;
        arrowTop = rawArrowTop;
        msgStyle.left = arrowLeft + arrowWidth + 4;
        msgStyle.top = Math.max(edgePadding, Math.min(rawArrowTop - 20, window.innerHeight - 150));
        arrowTransform = "rotate(90deg) scaleX(-1)";
    } else {
        // Default: top
        arrowTop = cutout.y - arrowHeight - 2;
        const rawMsgLeft = cutout.x + cutout.w / 2 - 20;
        arrowLeft = rawMsgLeft + 4;
        msgStyle.left = Math.max(edgePadding, Math.min(rawMsgLeft, window.innerWidth - cardMaxW - edgePadding));
        msgStyle.bottom = window.innerHeight - arrowTop + 4;
        arrowTransform = "scaleX(-1)";
    }


    return createPortal(
        <div
            className="fixed inset-0 z-[9999] cursor-pointer"
            onClick={handleClick}
            style={{ animation: "spotlight-fade-in 0.3s ease-out", ...(passThrough && { pointerEvents: "none" }) }}
        >
            <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
                <defs>
                    <mask id="spotlight-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <rect
                            x={cutout.x}
                            y={cutout.y}
                            width={cutout.w}
                            height={cutout.h}
                            rx={cutout.r}
                            ry={cutout.r}
                            fill="black"
                        />
                        {hlCutout && (
                            <rect
                                x={hlCutout.x}
                                y={hlCutout.y}
                                width={hlCutout.w}
                                height={hlCutout.h}
                                rx={hlCutout.r}
                                ry={hlCutout.r}
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.6)"
                    mask="url(#spotlight-mask)"
                />
                <rect
                    x={cutout.x}
                    y={cutout.y}
                    width={cutout.w}
                    height={cutout.h}
                    rx={cutout.r}
                    ry={cutout.r}
                    fill="none"
                    stroke="#0062ff"
                    strokeWidth="2"
                    className="animate-pulse"
                />
                {hlCutout && (
                    <rect
                        x={hlCutout.x}
                        y={hlCutout.y}
                        width={hlCutout.w}
                        height={hlCutout.h}
                        rx={hlCutout.r}
                        ry={hlCutout.r}
                        fill="none"
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="1.5"
                        strokeDasharray="6 4"
                    />
                )}
            </svg>

            {hlCutout && (
                <div
                    className="absolute"
                    style={{
                        left: hlCutout.x,
                        top: hlCutout.y,
                        width: hlCutout.w,
                        height: hlCutout.h,
                        borderRadius: hlCutout.r,
                        pointerEvents: "auto",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDismiss();
                    }}
                />
            )}

            <img
                src={spotlightArrowSvg}
                alt=""
                className="absolute"
                style={{
                    left: arrowLeft,
                    top: arrowTop,
                    width: arrowWidth,
                    height: arrowHeight,
                    pointerEvents: "none",
                    transform: arrowTransform,
                    animation: "spotlight-fade-in 0.4s ease-out 0.2s both",
                }}
            />

            {(message || onNext || onSkip) && (
                <div
                    className="absolute bg-white rounded-xl shadow-lg px-4 py-3"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        ...msgStyle,
                        pointerEvents: (onNext || onSkip) ? "auto" : "none",
                    }}
                >
                    {message && (
                        <p
                            className="text-sm leading-snug font-medium"
                            style={{ color: '#111827' }}
                        >
                            {message}
                        </p>
                    )}
                    {(onNext || onSkip) && (
                        <div className="flex items-center gap-2 mt-2">
                            {onSkip && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onSkip(); }}
                                    className="px-3 py-0.5 text-[11px] font-semibold rounded-full transition-all active:scale-95"
                                    style={{ backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' }}
                                >
                                    {skipLabel}
                                </button>
                            )}
                            {onNext && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onNext(); }}
                                    className="px-3 py-0.5 text-[11px] font-semibold rounded-full transition-all active:scale-95 text-white"
                                    style={{ backgroundColor: '#0062ff' }}
                                >
                                    {nextLabel}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}


            <style>{`
        @keyframes spotlight-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
        </div>,
        document.body,
    );
};